'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Neutrogena Have Microplastics? All 140 Published Formulas Checked (2026)"
      author="MicroPlastics Research Desk"
      date="August 10, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Does Neutrogena have microplastics: 140 published Neutrogena ingredient lists audited for solid synthetic polymer particles, split by skincare, makeup and haircare"
    >
      <BlogLastUpdated iso="2026-08-10" formatted="August 10, 2026" />

      <BlogTLDR>
        <strong>38 of the 140 Neutrogena formulas that publish an ingredient
        list contain a solid synthetic polymer particle — and the concentration
        is the story, not the total.</strong> Split by category it is{' '}
        <strong>18 of 72 skincare products (25%)</strong>,{' '}
        <strong>20 of 54 makeup products (37%)</strong>, and{' '}
        <strong>zero of 14 haircare products</strong>. Then the sharp bit:{' '}
        <strong>12 of those 18 skincare hits are Hydro Boost</strong> — the line
        Neutrogena sells on water, hyaluronic acid and weightlessness is
        two-thirds of its skincare polymer problem. Strip Hydro Boost out and
        the rest of the range is close to clean, with only three sunscreens, two
        Body Clear products and one retinol serum left. So this is not a brand
        to avoid. It is <strong>one product line to avoid</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>38 of 140</strong> published Neutrogena formulas contain a solid synthetic polymer particle (27%).</>,
          <>By category: <strong>skincare 18/72 (25%)</strong>, <strong>makeup 20/54 (37%)</strong>, <strong>haircare 0/14</strong>.</>,
          <><strong>Hydro Boost is 12 of the 18 skincare hits</strong> — two-thirds of the problem sits in one line.</>,
          <>Every Neutrogena <strong>shampoo and conditioner came back clean</strong> of solid particles.</>,
          <><strong>Acrylates copolymer is the workhorse</strong>, appearing in 17 of the 38.</>,
          <>Outside Hydro Boost, skincare hits are just <strong>three sunscreens, two Body Clear products and one retinol serum</strong>.</>,
          <>At 25%, Neutrogena skincare is still <strong>four times</strong> <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe</Link> and <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">Cetaphil</Link>, both at 6%.</>,
        ]}
      />

      <KeyStats
        heading="The Neutrogena audit"
        stats={[
          { value: '140', label: 'formulas with published INCI', context: 'of 250 product URLs walked on neutrogena.com in August 2026', source: 'MicroPlastics Research Desk audit' },
          { value: '38', label: 'contain a solid polymer particle', context: '27% overall, but very unevenly distributed', source: 'MicroPlastics Research Desk audit' },
          { value: '12 of 18', label: 'skincare hits that are Hydro Boost', context: 'one line accounting for two-thirds of the skincare problem', source: 'MicroPlastics Research Desk audit' },
          { value: '0 of 14', label: 'haircare products with a solid particle', context: 'every shampoo and conditioner audited came back clean', source: 'MicroPlastics Research Desk audit' },
        ]}
      />

      <h2>The answer depends entirely on which Neutrogena you own</h2>
      <p>
        A single brand-level number is close to useless here, and quoting the
        27% on its own would be the misleading way to report this. Neutrogena
        sells foundation and mascara next to moisturisers and shampoo, and those
        three categories behave completely differently.
      </p>

      <BlogComparisonTable
        caption="Neutrogena formulas containing a solid synthetic polymer particle, by category. From Neutrogena's own published ingredient lists, August 2026. Water-soluble polymers such as polyquaternium are excluded."
        columns={[
          { key: 'cat', label: 'Category' },
          { key: 'audited', label: 'Audited' },
          { key: 'hits', label: 'With solid polymer' },
          { key: 'rate', label: 'Rate' },
          { key: 'read', label: 'How to read it' },
        ]}
        rows={[
          { cat: 'Haircare', audited: '14', hits: '0', rate: '0%', read: 'Genuinely clean. Shampoos and conditioners contain polyquaternium, which dissolves and is not a particle.' },
          { cat: 'Skincare', audited: '72', hits: '18', rate: '25%', read: 'Four times CeraVe and Cetaphil — and two-thirds of it is one line.' },
          { cat: 'Makeup', audited: '54', hits: '20', rate: '37%', read: 'In line with the ~30% we found across prestige makeup in July. A colour-cosmetics norm, not a Neutrogena failing.' },
        ]}
      />

      <p>
        That haircare row is worth pausing on, because it is the opposite of
        what most people expect. Run a Neutrogena shampoo through an
        ingredient-checking app and it will almost certainly flag{' '}
        <strong>polyquaternium</strong>. That is a water-soluble cationic
        conditioning polymer — dissolved in the bottle, not suspended as solid
        particles — so it falls outside the definition of a synthetic polymer
        microparticle. We excluded it deliberately, and the entire haircare
        range comes back clean as a result.
      </p>

      <InlineGuessReveal
        source="neutrogena_hydro_boost_share"
        question="Neutrogena has 18 skincare products containing a solid polymer. How many are Hydro Boost?"
        options={[
          { label: 'Two or three' },
          { label: 'About half' },
          { label: 'Twelve', correct: true },
          { label: 'All eighteen' },
        ]}
        answer="twelve"
        answerNote={
          <>
            Two-thirds of Neutrogena&apos;s skincare polymer hits come from a
            single product line. The remaining six are three sunscreens, two
            Body Clear products and one retinol serum. If you do not use Hydro
            Boost, most of this article does not apply to you.
          </>
        }
        ctaText="Not sure which line yours is from? Scan the bottle and see."
        ctaLabel="Scan a product"
      />

      <RiskQuizInlineNudge source="blog_inline_neutrogena" />

      <h2>Hydro Boost: where the texture is the polymer</h2>
      <p>
        Hydro Boost is what Neutrogena has built its modern identity on — water
        gel, hyaluronic acid, weightless hydration, the blue jar. It is also the
        most polymer-dense thing the company sells.
      </p>

      <BlogComparisonTable
        caption="Every Hydro Boost product containing a solid synthetic polymer particle (neutrogena.com, August 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Polymer' },
          { key: 'type', label: 'Rinse-off / leave-on' },
        ]}
        rows={[
          { product: 'Hydro Boost Water Gel & Oil-Free Face Moisturizer', polymer: 'Dimethicone/vinyl dimethicone crosspolymer', type: 'Leave-on' },
          { product: 'Hydro Boost Gel-Cream for Eyes', polymer: 'Dimethicone/vinyl dimethicone crosspolymer', type: 'Leave-on' },
          { product: 'Hydro Boost+ Fragrance Free Niacinamide Serum', polymer: 'Dimethicone/vinyl dimethicone crosspolymer', type: 'Leave-on' },
          { product: 'Hydro Boost Night Pressed Face Serum', polymer: 'Dimethicone/vinyl dimethicone crosspolymer', type: 'Leave-on' },
          { product: 'Hydro Boost City Shield Water Gel SPF 25', polymer: 'Methyl methacrylate crosspolymer', type: 'Leave-on' },
          { product: 'Hydro Boost Hydrating Lip Sleeping Mask', polymer: 'Silica dimethyl silylate', type: 'Leave-on' },
          { product: 'Hydro Boost Hydrating Gel Cleanser', polymer: 'Acrylates copolymer', type: 'Rinse-off' },
          { product: 'Hydro Boost Gel Cleanser, 5 oz and 7.8 oz', polymer: 'Acrylates copolymer', type: 'Rinse-off' },
          { product: 'Hydro Boost Cleansing Gel (refill and pouch)', polymer: 'Acrylates copolymer', type: 'Rinse-off' },
          { product: 'Hydro Boost Soothing Milk Cleanser', polymer: 'Acrylates copolymer', type: 'Rinse-off' },
          { product: 'Hydro Boost Exfoliating Cleanser', polymer: 'Acrylates crosspolymer', type: 'Rinse-off' },
          { product: 'Hydro Boost Hydrating Concealer', polymer: 'Methyl methacrylate crosspolymer, polyethylene', type: 'Leave-on (makeup)' },
          { product: 'Hydro Boost Hydrating Tint', polymer: 'Dimethicone/vinyl dimethicone crosspolymer', type: 'Leave-on (makeup)' },
        ]}
      />

      <p>
        This is not hypocrisy on Neutrogena&apos;s part so much as chemistry
        catching up with a marketing promise.{' '}
        <strong>That bouncy, non-greasy, water-like gel is difficult to build
        with oils.</strong> The industry-standard route to it is a polymer
        network — dimethicone crosspolymers and acrylates copolymer — that holds
        water in a gel and breaks under shear so the product feels light going
        on. <strong>The texture the line is sold on is the polymer.</strong> You
        cannot have that sensation without something doing that job.
      </p>

      <h2>The six skincare hits that are not Hydro Boost</h2>
      <p>
        Everything else in Neutrogena skincare comes down to six products, and
        they fall into an obvious pattern:
      </p>
      <ul>
        <li>
          <strong>Three sunscreens</strong> — Clear Face Serum Sunscreen SPF
          60+, Invisible Daily Defense SPF 60+, and Mineral Ultra Sheer
          Dry-Touch SPF 30. All use acrylates copolymer, two add polyurethane.
          This is the same pattern we found at{' '}
          <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">Cetaphil</Link>, where
          three of five hits were SPF, and at{' '}
          <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe</Link>. Sunscreen
          has to protect <em>and</em> not feel like sunscreen, and polymers
          deliver the second promise.
        </li>
        <li>
          <strong>Two Body Clear products</strong> — the Pink Grapefruit acne
          wash and the salicylic acid body scrub, both acrylates copolymer. Both
          rinse-off.
        </li>
        <li>
          <strong>One retinol serum</strong> — Rapid Tone Repair Retinol +
          Vitamin C Dark Spot Corrector, containing nylon-12.
        </li>
      </ul>
      <p>
        Notably absent from that list: the Rapid Wrinkle Repair range, the Deep
        Clean cleansers, and the Ultra Sheer sunscreens other than the mineral
        one. Neutrogena&apos;s most famous products are largely not the problem.
      </p>

      <h2>The one polymer doing most of the work</h2>
      <p>
        Across all 38 hits, one ingredient dominates:{' '}
        <strong>acrylates copolymer, in 17 of them.</strong> After that come the
        dimethicone crosspolymers (9 each for the two variants), styrene/
        acrylates copolymer (6) and polyethylene (4).
      </p>
      <p>
        That matters practically. If you only ever learn to spot one thing on a
        Neutrogena label, <strong>&ldquo;acrylates&rdquo; is the word</strong> —
        it appears in nearly half the affected products, across cleansers,
        sunscreens and body washes alike.
      </p>

      <BlogAppCTA
        headline="Check which line yours is from"
        sub="Scan any Neutrogena moisturiser, cleanser or SPF with MicroPlastics for its polymers, a 0–100 score, and a cleaner product in the same category."
      />

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>If you use Hydro Boost, that is the whole conversation.</strong>{' '}
          Twelve products, and the gel cleansers rinse straight into the water
          system.
        </li>
        <li>
          <strong>Swap the cleanser before the moisturiser.</strong> A
          rinse-off product sends everything in it down the drain, so it is the
          bigger environmental lever; the Water Gel moisturiser is the bigger
          personal-exposure one.
        </li>
        <li>
          <strong>Check your SPF.</strong> Three of the six non-Hydro-Boost
          hits are sunscreens, and this holds across all three drugstore brands.
        </li>
        <li>
          <strong>Do not stop wearing sunscreen.</strong> The skin-cancer
          evidence is not close. Change product, not habit.
        </li>
        <li>
          <strong>Keep your shampoo.</strong> Neutrogena haircare came back
          clean, whatever your ingredient app says about polyquaternium.
        </li>
      </ul>

      <p>
        See the full{' '}
        <Link href="/blog/drugstore-skincare-microplastics-audited" className="text-forest underline">290-formula drugstore audit</Link>,
        or the two brands that scored far better:{' '}
        <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe</Link>{' '}and{' '}
        <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">Cetaphil</Link>.
        For the makeup comparison,{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">the July audit of 81 prestige formulas</Link>.
      </p>

      <p>
        A second dataset: the{' '}
        <Link href="/brands/neutrogena" className="text-forest underline">
          Neutrogena brand page
        </Link>{' '}
        shows what Open Beauty Facts lists on Neutrogena ingredient labels, a
        crowdsourced and Europe-weighted sample.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does Neutrogena have microplastics?',
            answer: 'Thirty-eight of the 140 Neutrogena formulas that publish an ingredient list contain a solid synthetic polymer particle. Split by category that is 18 of 72 skincare products (25%), 20 of 54 makeup products (37%) and zero of 14 haircare products. The concentration matters more than the total: 12 of the 18 skincare hits are Hydro Boost products.',
          },
          {
            question: 'Is Neutrogena Hydro Boost bad for microplastics?',
            answer: 'It is the most polymer-dense line Neutrogena sells. Twelve Hydro Boost products contain a solid synthetic polymer, which is two-thirds of the brand’s skincare hits. The reason is textural: the weightless water-gel feel the line is marketed on is produced by dimethicone crosspolymer and acrylates copolymer networks that hold water and break under shear. The polymer is not an additive to that sensation, it creates it.',
          },
          {
            question: 'Is Neutrogena shampoo free of microplastics?',
            answer: 'Yes, on solid particles. All 14 Neutrogena haircare products we audited came back clean. They do contain polyquaternium, which many ingredient-checking apps flag as plastic, but it is a water-soluble cationic conditioning polymer rather than a solid particle, so it sits outside the EU definition of synthetic polymer microparticles.',
          },
          {
            question: 'Is Neutrogena worse than CeraVe or Cetaphil?',
            answer: 'On skincare, yes, by roughly four times. Neutrogena skincare runs 18 of 72 formulas (25%) against CeraVe’s 4 of 71 and Cetaphil’s 5 of 79, both about 6%. Its overall 27% figure is partly inflated by a makeup range that CeraVe and Cetaphil do not have, but splitting the catalogue by category does not close the gap on skincare alone.',
          },
          {
            question: 'Which Neutrogena products are clean?',
            answer: 'Most of them. Outside Hydro Boost, only six skincare products contain a solid polymer particle: three sunscreens (Clear Face Serum SPF 60+, Invisible Daily Defense SPF 60+, Mineral Ultra Sheer SPF 30), two Body Clear products and the Rapid Tone Repair retinol serum. The Rapid Wrinkle Repair range, the Deep Clean cleansers and the whole haircare range came back clean.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'MicroPlastics Research Desk', year: 2026, title: 'First-party INCI audit of 140 published Neutrogena formulas (product sitemap walked and ingredient lists extracted from neutrogena.com, August 2026)', publisher: 'MicroPlastics' },
          { authors: 'Kenvue (Neutrogena)', year: 2026, title: 'Neutrogena product ingredient listings', publisher: 'neutrogena.com', url: 'https://www.neutrogena.com/' },
          { authors: 'European Commission', year: 2023, title: 'Commission Regulation (EU) 2023/2055 restricting synthetic polymer microparticles (solid-particle definition; REACH Annex XVII entry 78)', publisher: 'Official Journal of the European Union', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />
    </BlogPostLayout>
  )
}
